import * as Dialog from "@radix-ui/react-dialog";

import { Button } from "@/components/ui/button";
import type { MasterControlOrgDryRun } from "@/hooks/useMasterControl";

type DryRunDeleteOrgModalProps = {
  open: boolean;
  preview: MasterControlOrgDryRun | null;
  targetId: string;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  confirming: boolean;
};

export function DryRunDeleteOrgModal({
  open,
  preview,
  targetId,
  onOpenChange,
  onConfirm,
  confirming,
}: DryRunDeleteOrgModalProps) {
  const members = preview?.members ?? [];
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-3xl -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border bg-background p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="text-lg font-semibold text-rose-200">Preview organization deletion</Dialog.Title>
              <Dialog.Description className="text-sm text-muted-foreground">
                Review membership and related records before removing an organization.
              </Dialog.Description>
            </div>
            <button
              type="button"
              className="rounded-full border border-border p-1 text-foreground/80 hover:text-primary-foreground dark:text-amber-200"
              onClick={() => onOpenChange(false)}
            >
              ×
            </button>
          </div>

          <div className="mt-4 space-y-4 text-sm text-foreground/80">
            <div className="rounded-xl border border-border bg-background/30 p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Organization</p>
              {preview?.exists ? (
                <div className="mt-2 grid gap-4 md:grid-cols-2">
                  <div>
                    <p className="text-xs text-muted-foreground">ID</p>
                    <p className="font-semibold">{preview.organization?.id ?? targetId}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Name</p>
                    <p>{preview.organization?.name ?? "—"}</p>
                  </div>
                </div>
              ) : (
                <p className="mt-2 text-muted-foreground">No organization found for {targetId}.</p>
              )}
            </div>

            <div className="rounded-xl border border-border bg-background/30 p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Members</p>
                <span className="text-xs text-muted-foreground">{members.length} linked</span>
              </div>
              {members.length ? (
                <ul className="mt-3 space-y-2 text-sm text-foreground/80">
                  {members.slice(0, 5).map((member) => (
                    <li key={member.id} className="rounded-lg border border-border/80 bg-background/30 p-2">
                      <p className="font-semibold text-foreground">{member.email ?? member.id}</p>
                      <p className="text-xs text-muted-foreground">
                        {member.name ?? "Unknown"} • {member.role ?? "member"}
                      </p>
                    </li>
                  ))}
                  {members.length > 5 && (
                    <li className="text-xs text-muted-foreground">+{members.length - 5} more members</li>
                  )}
                </ul>
              ) : (
                <p className="mt-3 text-muted-foreground">No active members.</p>
              )}
            </div>

            <div className="rounded-xl border border-border bg-background/30 p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Related records</p>
              <dl className="mt-2 grid gap-4 md:grid-cols-3">
                <div>
                  <dt className="text-xs text-muted-foreground">Venues</dt>
                  <dd className="text-lg font-semibold text-foreground">{preview?.related.venues ?? 0}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">CRM contacts</dt>
                  <dd className="text-lg font-semibold text-foreground">{preview?.related.crm_contacts ?? 0}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Tasks</dt>
                  <dd className="text-lg font-semibold text-foreground">{preview?.related.tasks ?? 0}</dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={confirming || !preview?.exists}
              onClick={() => {
                void onConfirm();
              }}
            >
              {confirming ? "Deleting…" : "Confirm deletion"}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default DryRunDeleteOrgModal;
